// Die Saetze der Suchhinweise an EINER Stelle (ST-SEA-002, „PQL S1“;
// NFR-UX-003, NFR-UX-005, CM-UX-010).
//
// Jeder Satz nennt Problem und naechsten Schritt. Eingabeteile sind eigene
// Teile, damit der Bildschirm sie in Festbreitenschrift setzt, ohne Rahmen und
// ohne Anfuehrungszeichen drumherum (DESIGN P3-2). Die Woerter Feld, Wert,
// Bedingung und jedes Technikwort fehlen; es heisst „Angabe“ oder die Sache
// selbst (Stichwort, PLZ). Ein Knopf sagt, was er uebernimmt (DESIGN P3-6).

import type { Span } from './baum.ts';
import type { QueryHint } from './syntax.ts';
import { searchTermProblem } from '../../parties.ts';

export type MessagePart = { readonly kind: 'text'; readonly text: string } | { readonly kind: 'input'; readonly text: string };

export interface HintAction {
  /** Sichtbarer und zugaenglicher Name, etwa „stichwort übernehmen“. */
  readonly label: string;
  readonly span: Span;
  readonly replacement: string;
}

export interface HintMessage {
  readonly parts: readonly MessagePart[];
  readonly action?: HintAction | undefined;
}

const text = (value: string): MessagePart => ({ kind: 'text', text: value });
const input = (value: string): MessagePart => ({ kind: 'input', text: value });

/** Ein Beispiel je Feld fuer „Hinter stichwort: fehlt …“. */
const EXAMPLES: Readonly<Record<string, string>> = Object.freeze({
  stichwort: 'stichwort:Presse',
  haus: 'haus:"Verlag Nord"',
  verlag: 'verlag:"Verlag Nord"',
  rolle: 'rolle:Journalistin',
  status: '-status:inaktiv',
  ort: 'ort:München',
  plz: 'plz:80',
});

function example(subject: string | undefined): string {
  return EXAMPLES[(subject ?? '').toLocaleLowerCase('de-DE')] ?? 'stichwort:Presse';
}

function replace(hint: QueryHint, label: string): HintAction | undefined {
  return hint.suggestion === undefined ? undefined : { label, span: hint.span, replacement: hint.suggestion };
}

export function hintMessage(hint: QueryHint): HintMessage {
  const subject = hint.subject ?? '';
  switch (hint.code) {
    case 'quote_unclosed':
      return {
        parts: [text('Hinter '), input(hint.quote.replace(/^"/u, '')), text(' fehlt das schließende Anführungszeichen. Setze '), input('"'), text(' ans Ende.')],
      };
    case 'paren_unclosed':
      return { parts: [text('Eine Klammer wird nicht geschlossen. Ergänze '), input(')'), text(' am Ende oder entferne die '), input('('), text('.')] };
    case 'paren_unexpected':
      return { parts: [text('Hier schließt eine Klammer, die nie geöffnet wurde. Entferne die '), input(')'), text('.')] };
    case 'parens_empty':
      return { parts: [text('Die Klammern sind leer. Schreibe hinein, wonach gesucht wird, oder entferne sie.')] };
    case 'value_missing':
      return {
        parts: [text('Hinter '), input(`${subject}:`), text(' fehlt, wonach gesucht wird. Tippe etwa '), input(example(subject)), text('.')],
      };
    case 'operator_dangling':
      return {
        parts: [text('Bei '), input(hint.quote), text(' fehlt eine Seite. Ergänze davor und dahinter eine Angabe oder entferne '), input(hint.quote), text('.')],
      };
    case 'field_unknown':
      return {
        parts: [input(hint.quote), text(' gibt es nicht. Meintest du '), input(hint.suggestion ?? ''), text('?')],
        action: replace(hint, `${hint.suggestion ?? ''} übernehmen`),
      };
    case 'colon_text':
      return {
        parts: [input(hint.quote), text(' wird als Name gesucht. Eingrenzen geht etwa mit '), input('stichwort:'), text(', '), input('ort:'), text(' oder '), input('plz:'), text('; die Suchhilfe nennt alle.')],
      };
    case 'lowercase_operator':
      return {
        parts: [text('Meintest du '), input(hint.suggestion ?? ''), text('? Nur großgeschrieben verbindet es zwei Angaben.')],
        action: replace(hint, `${hint.suggestion ?? ''} übernehmen`),
      };
    case 'field_later':
      return { parts: [text('Mit '), input(`${subject}:`), text(' lässt sich hier noch nicht suchen. Das kommt später.')] };
    case 'or_across_fields':
      return {
        parts: [text('ODER zwischen verschiedenen Angaben geht hier noch nicht. Suche nacheinander und sammle die Treffer in einer Sammlung.')],
      };
    case 'not_open_field':
      return {
        parts: [text('Ausschließen geht hier nur mit '), input('-status:inaktiv'), text('. Suche nach dem, was enthalten sein soll.')],
      };
    case 'field_twice':
      return {
        parts: [input(`${subject}:`), text(' steht zweimal. Verbinde beides mit ODER, etwa '), input(`${subject}:(A ODER B)`), text('.')],
      };
    case 'several_values':
      return { parts: [text('Bei '), input(`${subject}:`), text(' geht hier nur eine Angabe. Entferne die übrigen.')] };
    case 'comparison_unsupported':
      return subject.toLocaleLowerCase('de-DE') === 'plz'
        ? { parts: [text('Bei '), input('plz:'), text(' geht kein Vergleich. Tippe den Anfang der PLZ, etwa '), input('plz:80'), text('.')] }
        : { parts: [text('Bei '), input(`${subject}:`), text(' geht hier kein Vergleich. Lass ihn weg.')] };
    case 'status_unsupported':
      return {
        parts: [text('Diese Auswahl des Zustands geht im Suchfeld noch nicht. Inaktive blendest du mit '), input('-status:inaktiv'), text(' aus; einbeziehen kannst du sie im Filter.')],
      };
    case 'role_unknown':
      return hint.suggestion === undefined
        ? { parts: [text('Keine Rolle heißt '), input(hint.quote), text('. Die Suchhilfe nennt alle Rollen.')] }
        : {
            parts: [text('Keine Rolle heißt '), input(hint.quote), text('. Meintest du '), input(hint.suggestion), text('?')],
            action: replace(hint, `${hint.suggestion} übernehmen`),
          };
    case 'postal_code_form':
      return { parts: [text('Die PLZ besteht nur aus Ziffern, höchstens fünf, etwa '), input('plz:80'), text('.')] };
    case 'locality_length':
      return { parts: [text('Gib für den Ort mindestens zwei und höchstens hundert Zeichen ein, etwa '), input('ort:München'), text('.')] };
    case 'text_split':
      return {
        parts: [text('Der Name steht an zwei Stellen. Schreibe ihn zusammen vor die übrigen Angaben, etwa '), input('Müller Söhne stichwort:Presse'), text('.')],
      };
    case 'text_length':
      return { parts: [text(searchTermProblem(hint.quote) ?? 'Gib mindestens drei Zeichen ein.')] };
    case 'separator_missing':
      return {
        parts: [text('Zwischen den Angaben in '), input(hint.quote), text(' fehlt ODER. Ergänze es, oder setze ein Stichwort aus mehreren Wörtern in Anführungszeichen.')],
        action: replace(hint, 'ODER übernehmen'),
      };
    case 'query_too_long':
      return { parts: [text('Die Suche ist zu lang. Kürze sie auf höchstens fünfhundert Zeichen.')] };
    case 'no_search_reason':
      return { parts: [text('Damit allein lässt sich nicht suchen. Ergänze einen Namen, ein Stichwort oder einen Ort.')] };
  }
}

/** Der Satz als Klartext, fuer Proben und Ansagen. */
export function hintPlainText(hint: QueryHint): string {
  return hintMessage(hint)
    .parts.map((part) => part.text)
    .join('');
}

/** Wendet einen Knopf an: das Wort wird ersetzt, der Rest bleibt stehen. */
export function applyAction(source: string, action: HintAction): string {
  return source.slice(0, action.span.from) + action.replacement + source.slice(action.span.to);
}
